/**
 * Pure, unit-tested logic behind the orders kanban view. Kept out of the
 * React component so grouping/sum/legality math is testable without the DOM,
 * mirroring offersBoard.ts.
 *
 * Orders are NOT free-movement like offers: drag&drop only offers the exact
 * transitions the backend FSM allows (OrderStatus.nextAllowed mirror below),
 * for callers holding orders:transition — the same moves as the detail
 * page's buttons, so the permanent approval gate is untouched.
 */
import type { Order, OrderStatus } from "../api/orders";

/**
 * FE mirror of the backend FSM (OrderStatus.nextAllowed). The server remains
 * the authority — an illegal move still 409s — this only decides which board
 * drops are offered at all.
 */
export const NEXT_ALLOWED: Record<OrderStatus, readonly OrderStatus[]> = {
  DRAFT: ["SUBMITTED", "CANCELLED"],
  SUBMITTED: ["IN_REVIEW", "CANCELLED"],
  IN_REVIEW: ["APPROVED", "REJECTED", "CANCELLED"],
  APPROVED: ["SHIPPED", "CANCELLED"],
  SHIPPED: ["INVOICED"],
  REJECTED: [],
  CANCELLED: [],
  INVOICED: [],
};

/** True when the FSM allows moving an order from `from` to `to`. */
export function canDropTo(from: OrderStatus, to: OrderStatus): boolean {
  return NEXT_ALLOWED[from].includes(to);
}

/**
 * The columns a card may be dragged to: the FSM-legal moves, without the approval for a viewer
 * who may not approve or who entered the order. An order carried over from the ERP moves only
 * in the ERP, so its card is not dragged at all.
 */
export function boardTargets(
  order: Pick<Order, "status" | "createdBy" | "fromErp">,
  canApprove: boolean,
  viewerId: string | null,
): OrderStatus[] {
  if (order.fromErp) return [];
  return NEXT_ALLOWED[order.status].filter((to) => to !== "APPROVED" || (canApprove && order.createdBy !== viewerId));
}

/**
 * The order lifecycle in board-column order. DRAFT→INVOICED is the happy path
 * (always shown as columns); REJECTED/CANCELLED are terminal side-exits shown
 * only when they actually hold orders, so a clean pipeline isn't cluttered by
 * two usually-empty columns.
 */
export const BOARD_STATUS_ORDER: OrderStatus[] = [
  "DRAFT", "SUBMITTED", "IN_REVIEW", "APPROVED", "SHIPPED", "INVOICED", "REJECTED", "CANCELLED",
];

/** The happy-path statuses that are always rendered as columns even when empty. */
export const CORE_BOARD_STATUSES: ReadonlySet<OrderStatus> = new Set<OrderStatus>([
  "DRAFT", "SUBMITTED", "IN_REVIEW", "APPROVED", "SHIPPED", "INVOICED",
]);

export type OrderColumn = {
  status: OrderStatus;
  /** Vrh kolone, kako ga je vratio server. */
  orders: Order[];
  /** Broj nad celim vidljivim skupom, ne nad učitanim karticama. */
  count: number;
};

/**
 * Koje kolone tabla prikazuje i šta u njima stoji.
 *
 * Kartice i brojeve daje server, po koloni. Ranije se tabla gradila iz učitane stranice, pa je
 * nad 44.307 porudžbina govorila o pedeset: kolona je imala najviše onoliko kartica koliko ih je
 * na toj stranici, a broj uz naziv bio je dužina te kolone.
 *
 * Kolona se prikazuje ako je deo redovnog toka, ako u njoj nečega ima, ili ako je dozvoljeno
 * odredište za ono što se upravo vuče — tako prazan `REJECTED` osvane kao meta tek dok se vuče
 * porudžbina koja tamo sme.
 */
export function boardColumns(
  poStatusu: Record<string, Order[]>,
  brojevi: Record<string, number> | null,
  extraStatuses?: ReadonlySet<OrderStatus>,
): OrderColumn[] {
  return BOARD_STATUS_ORDER
    .filter((st) => CORE_BOARD_STATUSES.has(st)
      || (brojevi?.[st] ?? 0) > 0
      || (poStatusu[st]?.length ?? 0) > 0
      || extraStatuses?.has(st))
    .map((status) => ({
      status,
      orders: poStatusu[status] ?? [],
      // Bez servera pada na dužinu učitanog, da kolona ne ostane bez broja.
      count: brojevi?.[status] ?? (poStatusu[status]?.length ?? 0),
    }));
}
